import React from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaShieldAlt, FaLock, FaEnvelope, FaMapMarkerAlt, FaUserShield, FaDatabase, FaCookie, FaGlobe } from 'react-icons/fa';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A1929] to-[#1E3A5F]">
      {/* Header */}
      <div className="bg-[#1A2942] border-b border-[#2D5A8F] sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/register" className="flex items-center text-gray-300 hover:text-[#FFD700] transition-colors">
            <FaArrowLeft className="mr-2" />
            <span>Back to Registration</span>
          </Link>
          <div className="flex items-center space-x-2">
            <FaShieldAlt className="text-[#FFD700]" />
            <span className="text-white font-bold">Arth Gurukul</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl border border-[#2D5A8F] p-8 shadow-2xl">
          {/* Title */}
          <div className="text-center mb-8 pb-6 border-b border-[#2D5A8F]">
            <div className="flex items-center justify-center mb-4">
              <FaLock className="text-[#FFD700] text-4xl" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">PRIVACY POLICY</h1>
            <h2 className="text-xl text-[#FFD700] font-semibold">Arth Gurukul</h2>
            <p className="text-gray-400">(An Initiative of MDRT School)</p>
            <div className="mt-4 flex items-center justify-center space-x-4 text-sm text-gray-400">
              <span>Effective Date: 27th Feb 2026</span>
              <span className="text-[#FFD700]">|</span>
              <span>Version: 1.0</span>
            </div>
          </div>

          {/* Content */}
          <div className="prose prose-invert max-w-none space-y-8 text-gray-300">
            
            {/* Section 1 */}
            <section>
              <h3 className="text-xl font-bold text-[#FFD700] mb-3">1. Introduction</h3>
              <p>Arth Gurukul ("we", "us", "our", "Platform") is committed to protecting and respecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your personal data when you use our financial planning platform.</p>
              <p>This Policy is compliant with the <span className="text-white font-semibold">Digital Personal Data Protection Act, 2023 (DPDP Act)</span> and other applicable Indian laws.</p>
              <p className="font-semibold text-white">By using our Platform, you consent to the practices described in this Privacy Policy.</p>
            </section>

            {/* Section 2 */}
            <section>
              <h3 className="text-xl font-bold text-[#FFD700] mb-3 flex items-center">
                <FaDatabase className="mr-2" /> 2. Information We Collect
              </h3>
              <p>We collect the following categories of personal data:</p>
              
              <div className="mt-4 space-y-4">
                <div className="bg-[#0F172A] rounded-lg p-4 border border-[#2D5A8F]">
                  <h4 className="text-white font-bold mb-2">2.1 Identity Information</h4>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Full name</li>
                    <li>Date of birth and age</li>
                    <li>PAN number (for KYC purposes)</li>
                    <li>Contact details (email, phone number, address)</li>
                    <li>Occupation and employment details</li>
                  </ul>
                </div>

                <div className="bg-[#0F172A] rounded-lg p-4 border border-[#2D5A8F]">
                  <h4 className="text-white font-bold mb-2">2.2 Financial Information</h4>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Income details (salary, rental, business income)</li>
                    <li>Expense information</li>
                    <li>Assets (savings, investments, property, gold)</li>
                    <li>Liabilities (loans, EMIs, credit card dues)</li>
                    <li>Insurance policies and coverage</li>
                    <li>Financial goals and risk profile</li>
                  </ul>
                </div>

                <div className="bg-[#0F172A] rounded-lg p-4 border border-[#2D5A8F]">
                  <h4 className="text-white font-bold mb-2">2.3 Family Information</h4>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Spouse details (name, date of birth, occupation)</li>
                    <li>Children details (name, age, education goals)</li>
                    <li>Number of dependents</li>
                  </ul>
                </div>

                <div className="bg-[#0F172A] rounded-lg p-4 border border-[#2D5A8F]">
                  <h4 className="text-white font-bold mb-2">2.4 Technical Information</h4>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>IP address and device information</li>
                    <li>Browser type and version</li>
                    <li>Login timestamps and session data</li>
                    <li>Usage patterns and preferences</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 3 */}
            <section>
              <h3 className="text-xl font-bold text-[#FFD700] mb-3">3. Purpose of Data Collection</h3>
              <p>We use your personal data for the following purposes:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>To provide goal-based financial planning services</li>
                <li>To generate financial reports, gap analysis, and projections</li>
                <li>To enable your assigned financial advisor to serve you better</li>
                <li>To communicate important updates, notifications, and announcements</li>
                <li>To improve our platform and user experience</li>
                <li>To comply with legal and regulatory requirements</li>
                <li>To prevent fraud and ensure platform security</li>
              </ul>
            </section>

            {/* Section 4 */}
            <section>
              <h3 className="text-xl font-bold text-[#FFD700] mb-3">4. Legal Basis for Processing</h3>
              <p>We process your personal data based on:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li><span className="text-white font-medium">Consent:</span> You provide explicit consent during registration</li>
                <li><span className="text-white font-medium">Contract:</span> Processing necessary to provide our services</li>
                <li><span className="text-white font-medium">Legal Obligation:</span> Compliance with applicable laws</li>
                <li><span className="text-white font-medium">Legitimate Interest:</span> Improving our services and preventing fraud</li>
              </ul>
            </section>

            {/* Section 5 */}
            <section>
              <h3 className="text-xl font-bold text-[#FFD700] mb-3 flex items-center">
                <FaUserShield className="mr-2" /> 5. Data Sharing & Disclosure
              </h3>
              <p>We may share your personal data with:</p>
              
              <div className="mt-4 space-y-3">
                <div className="bg-green-500/10 border border-green-500/50 rounded-lg p-4">
                  <h4 className="text-green-400 font-bold mb-2">Authorized Sharing</h4>
                  <ul className="list-disc pl-6 space-y-1">
                    <li><span className="text-white">Your Assigned Financial Advisor:</span> To provide personalized financial planning</li>
                    <li><span className="text-white">Managers (if applicable):</span> For oversight and quality assurance</li>
                    <li><span className="text-white">Service Providers:</span> Cloud hosting, analytics (under strict confidentiality)</li>
                  </ul>
                </div>

                <div className="bg-yellow-500/10 border border-yellow-500/50 rounded-lg p-4">
                  <h4 className="text-yellow-400 font-bold mb-2">Legal Disclosure</h4>
                  <p>We may disclose data if required by law, court order, or government authority.</p>
                </div>

                <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4">
                  <h4 className="text-red-400 font-bold mb-2">We Do NOT</h4>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Sell your personal data to third parties</li>
                    <li>Share data for marketing without your consent</li>
                    <li>Transfer data outside India without adequate safeguards</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 6 */}
            <section>
              <h3 className="text-xl font-bold text-[#FFD700] mb-3">6. Data Security</h3>
              <p>We implement industry-standard security measures including:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Encryption of data in transit (HTTPS/TLS) and at rest</li>
                <li>Secure authentication with hashed passwords</li>
                <li>Role-based access controls</li>
                <li>Regular security audits and vulnerability assessments</li>
                <li>Secure cloud infrastructure</li>
              </ul>
              <p className="mt-4 text-yellow-400">While we strive to protect your data, no system is 100% secure. Please maintain the confidentiality of your login credentials.</p>
            </section>

            {/* Section 7 */}
            <section>
              <h3 className="text-xl font-bold text-[#FFD700] mb-3">7. Data Retention</h3>
              <p>We retain your personal data for:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li><span className="text-white font-medium">Active accounts:</span> As long as your account is active</li>
                <li><span className="text-white font-medium">Deleted accounts:</span> Up to 5 years for legal and regulatory compliance</li>
                <li><span className="text-white font-medium">Financial records:</span> As required by applicable tax and financial regulations</li>
              </ul>
              <p className="mt-4">You may request deletion of your data subject to legal retention requirements.</p>
            </section>

            {/* Section 8 */}
            <section>
              <h3 className="text-xl font-bold text-[#FFD700] mb-3 flex items-center">
                <FaCookie className="mr-2" /> 8. Cookies & Tracking Technologies
              </h3>
              <p>We use cookies and similar technologies for:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li><span className="text-white font-medium">Essential Cookies:</span> Required for platform functionality and authentication</li>
                <li><span className="text-white font-medium">Analytics Cookies:</span> To understand usage patterns and improve our services</li>
                <li><span className="text-white font-medium">Preference Cookies:</span> To remember your settings and preferences</li>
              </ul>
              <p className="mt-4">You can manage cookie preferences through your browser settings. Disabling essential cookies may affect platform functionality.</p>
            </section>

            {/* Section 9 */}
            <section>
              <h3 className="text-xl font-bold text-[#FFD700] mb-3">9. Your Rights Under DPDP Act</h3>
              <p>As a Data Principal, you have the right to:</p>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-[#0F172A] rounded-lg p-3 border border-[#2D5A8F]">
                  <h4 className="text-[#FFD700] font-bold text-sm">Right to Access</h4>
                  <p className="text-sm">Request a summary of your personal data</p>
                </div>
                <div className="bg-[#0F172A] rounded-lg p-3 border border-[#2D5A8F]">
                  <h4 className="text-[#FFD700] font-bold text-sm">Right to Correction</h4>
                  <p className="text-sm">Request correction of inaccurate data</p>
                </div>
                <div className="bg-[#0F172A] rounded-lg p-3 border border-[#2D5A8F]">
                  <h4 className="text-[#FFD700] font-bold text-sm">Right to Erasure</h4>
                  <p className="text-sm">Request deletion of your data</p>
                </div>
                <div className="bg-[#0F172A] rounded-lg p-3 border border-[#2D5A8F]">
                  <h4 className="text-[#FFD700] font-bold text-sm">Right to Withdraw Consent</h4>
                  <p className="text-sm">Withdraw consent at any time</p>
                </div>
                <div className="bg-[#0F172A] rounded-lg p-3 border border-[#2D5A8F]">
                  <h4 className="text-[#FFD700] font-bold text-sm">Right to Grievance Redressal</h4>
                  <p className="text-sm">Lodge complaints with Data Protection Board</p>
                </div>
                <div className="bg-[#0F172A] rounded-lg p-3 border border-[#2D5A8F]">
                  <h4 className="text-[#FFD700] font-bold text-sm">Right to Nominate</h4>
                  <p className="text-sm">Nominate a person to exercise rights</p>
                </div>
              </div>
              <p className="mt-4">To exercise these rights, contact our Grievance Officer (details below).</p>
            </section>

            {/* Section 10 */}
            <section>
              <h3 className="text-xl font-bold text-[#FFD700] mb-3 flex items-center">
                <FaGlobe className="mr-2" /> 10. Third-Party Links
              </h3>
              <p>Our Platform may contain links to third-party websites (e.g., Money School, educational resources). We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies.</p>
            </section>

            {/* Section 11 */}
            <section>
              <h3 className="text-xl font-bold text-[#FFD700] mb-3">11. Children's Privacy</h3>
              <p>Our Platform is not intended for individuals under 18 years of age. We do not knowingly collect personal data from minors. If you believe a minor has provided us with personal data, please contact us immediately.</p>
            </section>

            {/* Section 12 */}
            <section>
              <h3 className="text-xl font-bold text-[#FFD700] mb-3">12. Changes to This Policy</h3>
              <p>We may update this Privacy Policy from time to time. Material changes will be communicated via:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Email notification</li>
                <li>Dashboard notification</li>
                <li>Updated "Effective Date" on this page</li>
              </ul>
              <p className="mt-4">Continued use of the Platform after changes constitutes acceptance of the updated Policy.</p>
            </section>

            {/* Section 13 */}
            <section>
              <h3 className="text-xl font-bold text-[#FFD700] mb-3">13. Grievance Officer & Contact</h3>
              <p>For privacy-related queries, complaints, or to exercise your rights:</p>
              <div className="mt-4 bg-[#0F172A] rounded-lg p-4 border border-[#2D5A8F]">
                <p className="flex items-center mb-2">
                  <span className="text-gray-400 w-32">Grievance Officer:</span>
                  <span className="text-white font-medium">Jibal Bobby</span>
                </p>
                <p className="flex items-center mb-2">
                  <FaEnvelope className="text-[#FFD700] mr-2" />
                  <span className="text-gray-400 w-28">Email:</span>
                  <a href="mailto:privacy@mdrtschool.com" className="text-[#FFD700] hover:underline">privacy@mdrtschool.com</a>
                </p>
                <p className="flex items-center mb-2">
                  <span className="text-gray-400 w-32">Alternate Email:</span>
                  <a href="mailto:care@mdrtschool.com" className="text-[#FFD700] hover:underline">care@mdrtschool.com</a>
                </p>
                <p className="flex items-start">
                  <FaMapMarkerAlt className="text-[#FFD700] mr-2 mt-1 flex-shrink-0" />
                  <span className="text-gray-400 w-28 flex-shrink-0">Registered Office:</span>
                  <span className="text-white">Financial Nirvana, Benevolent Private Spaces LLP, Flat No. B-1002, Heritage Palace, WANAWADI, Road: KEDARI NAGAR, Pune, Maharashtra, 411040</span>
                </p>
              </div>
              <p className="mt-4">We aim to respond to all requests within <span className="text-white font-semibold">30 days</span>.</p>
            </section>

            {/* Section 14 */}
            <section>
              <h3 className="text-xl font-bold text-[#FFD700] mb-3">14. Governing Law</h3>
              <p>This Privacy Policy is governed by the laws of India, including the Digital Personal Data Protection Act, 2023.</p>
              <p className="font-semibold text-white mt-2">Courts located in Pune shall have exclusive jurisdiction for any disputes.</p>
            </section>

          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-[#2D5A8F] text-center">
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/terms-and-conditions" 
                className="inline-flex items-center px-4 py-2 bg-[#1E3A5F] text-white rounded-lg hover:bg-[#2D5A8F] transition-all border border-[#2D5A8F]"
              >
                View Terms & Conditions
              </Link>
              <Link 
                to="/register" 
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] transition-all"
              >
                <FaArrowLeft className="mr-2" />
                Back to Registration
              </Link>
            </div>
          </div>
        </div>

        {/* Page Footer */}
        <div className="mt-8 text-center text-gray-400 text-xs">
          <p>© 2026 Arth Gurukul. All rights reserved.</p>
          <p className="mt-1">An Initiative of MDRT School</p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
